import { ApiError, requestJson as defaultRequestJson } from "./api.js";

const plural = (count) => `${count} ${count === 1 ? "bottle" : "bottles"}`;
const copyItem = (item) => item ? { ...item } : null;

export function createCollectionController({
  document,
  auth,
  requestJson = defaultRequestJson,
  onAction = () => {},
}) {
  const items = new Map();
  const pending = new Set();
  const subscribers = new Set();
  const error = document.querySelector("#collectionError");
  const retry = document.querySelector("#retryCollectionAction");
  let retryAction = null;
  let started = false;

  const snapshot = () => new Map(
    [...items].map(([code, item]) => [code, { ...item }]),
  );

  function notify() {
    const next = snapshot();
    for (const subscriber of subscribers) subscriber(next);
  }

  function setError(message, action = null) {
    if (error) error.textContent = message;
    retryAction = action;
    if (retry) retry.hidden = !action;
  }

  function button(action, code, label, isPending) {
    const control = document.createElement("button");
    control.type = "button";
    control.dataset.collectionAction = action;
    control.dataset.bottle = code;
    if (action === "add") {
      control.setAttribute("aria-label", `${isPending ? "Adding" : "Add"} ${code} to collection`);
      control.textContent = isPending ? "Adding…" : "Add";
    } else {
      control.textContent = isPending ? `${label.replace(/ one$/, "")}…` : label;
    }
    control.disabled = isPending;
    return control;
  }

  function renderSlot(slot) {
    const code = slot.dataset.collectionBottle;
    const kind = slot.dataset.collectionKind ?? "browse";
    const item = items.get(code);
    const current = item?.currentBottleCount ?? 0;
    const past = item?.pastBottleCount ?? 0;
    const fragment = document.createDocumentFragment();
    const count = document.createElement("span");
    count.className = "collection-count";
    count.dataset.collectionCount = "";

    if (kind === "browse") {
      const key = `${code}:add`;
      fragment.append(button("add", code, "Add", pending.has(key)));
      if (current || past) {
        count.textContent = `${plural(current)} in Current Collection`
          + (past ? ` · ${plural(past)} in Past Bottles` : "");
      }
      fragment.append(count);
    } else {
      const bottleCount = kind === "current" ? current : past;
      count.textContent = plural(bottleCount);
      fragment.append(count);
      const actions = document.createElement("div");
      actions.className = "collection-item-actions";
      const definitions = kind === "current"
        ? [["finish", "Finish one"], ["remove-current", "Remove one"]]
        : [["restore", "Restore one"], ["remove-past", "Remove one"]];
      for (const [action, label] of definitions) {
        actions.append(button(action, code, label, pending.has(`${code}:${action}`)));
      }
      fragment.append(actions);
    }
    slot.replaceChildren(fragment);
  }

  function mount(root = document) {
    const slots = [...root.querySelectorAll("[data-collection-bottle]")];
    if (root.matches?.("[data-collection-bottle]")) slots.unshift(root);
    for (const slot of slots) renderSlot(slot);
  }

  function renderMounted() {
    mount(document);
  }

  async function refresh() {
    if (!auth.isSignedIn()) return snapshot();
    const response = await requestJson("/api/collection", { auth });
    items.clear();
    for (const item of response.items ?? []) items.set(item.bottleId, item);
    setError("");
    renderMounted();
    notify();
    return snapshot();
  }

  async function act(code, action, { silent = false } = {}) {
    if (!auth.isSignedIn()) {
      await auth.requireUser(() => act(code, action));
      return null;
    }
    const key = `${code}:${action}`;
    if (pending.has(key)) return null;
    const previousItem = copyItem(items.get(code));
    pending.add(key);
    setError("");
    renderMounted();
    try {
      const response = await requestJson(`/api/collection/${encodeURIComponent(code)}/actions`, {
        method: "POST",
        body: { action },
        auth,
      });
      if (response.item) items.set(code, response.item);
      else items.delete(code);
      renderMounted();
      notify();
      const result = { code, action, previousItem, item: copyItem(response.item) };
      if (!silent) onAction(result);
      return result;
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        await refresh();
        setError("Your collection changed elsewhere. It has been refreshed.");
      } else if (caught instanceof ApiError && caught.status === 401) {
        const resume = () => act(code, action);
        setError("Please sign in again to continue.");
        await auth.requireUser(resume, { force: true });
      } else {
        const retryCurrentAction = () => act(code, action);
        setError("Your collection could not be updated.", retryCurrentAction);
      }
      return null;
    } finally {
      pending.delete(key);
      renderMounted();
    }
  }

  function clearPrivateState() {
    items.clear();
    pending.clear();
    setError("");
    renderMounted();
    notify();
  }

  if (document.__smwsCollectionClickHandler) {
    document.removeEventListener("click", document.__smwsCollectionClickHandler);
  }
  const handleCollectionClick = (event) => {
    const control = event.target.closest?.("[data-collection-action][data-bottle]");
    if (!control) return;
    act(control.dataset.bottle, control.dataset.collectionAction);
  };
  document.__smwsCollectionClickHandler = handleCollectionClick;
  document.addEventListener("click", handleCollectionClick);
  retry?.addEventListener("click", () => retryAction?.());

  return {
    async start() {
      if (started) return snapshot();
      started = true;
      mount(document);
      auth.subscribe?.(({ signedIn }) => {
        if (signedIn) refresh().catch(() => setError("Your collection could not be loaded."));
        else clearPrivateState();
      });
      if (auth.isSignedIn()) await refresh();
      return snapshot();
    },
    mount,
    act,
    refresh,
    getItems: snapshot,
    subscribe(listener) {
      subscribers.add(listener);
      listener(snapshot());
      return () => subscribers.delete(listener);
    },
    clearPrivateState,
  };
}
